import styled from 'styled-components'
import { black, darkGrey, darkOrange, mutedGrey, surface2, textMuted } from '@/theme/colors'
import { media, motion, radii } from '@/theme/tokens'
import { text } from '@/theme/typography'
import { Button } from '@components/Button'
import { OptionRow } from '@components/OptionRow'
import { destinationColor } from '../destinationPalette'

/**
 * The legend's fixed geometry, shared with the paging maths: a row is one
 * height whatever it says, so how many fit is a division, not a measurement
 * of each.
 */
/** The air the highlight keeps around a row: above and below the colour bar, left of it, right of the dates. */
const LEGEND_ROW_PADDING = 6
/** Two text lines stand 40px, and so does the colour bar beside them. */
const LEGEND_BAR_HEIGHT = 40
export const LEGEND_ROW_HEIGHT = LEGEND_BAR_HEIGHT + 2 * LEGEND_ROW_PADDING
export const LEGEND_ROW_GAP = 4
const PAGER_BUTTON = 24
const PAGER_GAP = 4
export const LEGEND_PAGER_HEIGHT = PAGER_BUTTON + PAGER_GAP
/** Two rows and the pager: what the legend keeps under the calendar on a short screen. */
const MIN_STRETCHED_HEIGHT = 2 * LEGEND_ROW_HEIGHT + LEGEND_ROW_GAP + LEGEND_PAGER_HEIGHT

/**
 * Under the calendar (the wide card) the legend takes what is left of the
 * column: the block stretches to it and its content sits out of flow, so a long
 * destination list can never push the card taller. Stacked, it flows beside
 * the calendar and pages by a fixed count.
 *
 * Rows pad their highlight, so the block bleeds out by as much: the colour bars
 * still line up with the calendar's edge, and the highlight's corners clear the
 * body's clip.
 */
export const LegendBlock = styled.div`
	flex: 1 1 180px;
	min-width: 180px;
	margin: 0 -${LEGEND_ROW_PADDING}px;

	${media.aboveTabletWide} {
		position: relative;
		flex: 1 1 0;
		min-height: ${MIN_STRETCHED_HEIGHT}px;
	}
`

/** The rows and, when they overflow, the pager. Its height is what the paging measures. */
export const LegendBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${PAGER_GAP}px;

	${media.aboveTabletWide} {
		position: absolute;
		inset: 0;
		overflow: hidden;
	}
`

export const LegendList = styled.ul`
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: ${LEGEND_ROW_GAP}px;
`

export const LegendRowItem = styled.li`
	flex: none;
`

/**
 * One destination, and the way to its first day: an option row stripped to the
 * legend's look — no card, no hairline — where the selected day's destination
 * wears a faint ground and hover previews it. A flex row of a fixed height: the
 * bar stretches to exactly what the padding leaves, so the ground shows the
 * same air on every side of it whatever the text beside it measures.
 */
export const LegendRowButton = styled(OptionRow)`
	height: ${LEGEND_ROW_HEIGHT}px;
	gap: 12px;
	padding: ${LEGEND_ROW_PADDING}px;
	border: none;
	background: ${({ $selected }) => ($selected ? surface2 : 'transparent')};

	&:hover {
		background: ${surface2};
	}
`

export const ColorBar = styled.span<{ $colorIndex: number }>`
	flex: none;
	width: 3px;
	align-self: stretch;
	border-radius: ${radii.tiny};
	background: ${({ $colorIndex }) => destinationColor($colorIndex).fill};
`

/** The name over its detail; the dates sit outside it, centred against the pair. */
export const RowText = styled.div`
	flex: 1;
	min-width: 0;
`

export const RowName = styled.div`
	${text.labelSm}
	color: ${black};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

export const RowDetail = styled.div`
	${text.caption}
	color: ${textMuted};
	margin-top: 1px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

export const RowDates = styled.span`
	flex: none;
	${text.caption}
	color: ${mutedGrey};
	white-space: nowrap;
`

export const Pager = styled.nav`
	flex: none;
	height: ${PAGER_BUTTON}px;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
`

export const PagerButton = styled(Button).attrs({ variant: 'icon' as const })`
	width: ${PAGER_BUTTON}px;
	height: ${PAGER_BUTTON}px;
	flex: none;
`

export const PagerDots = styled.span`
	display: flex;
	align-items: center;
	gap: 6px;
`

export const PagerDot = styled.span<{ $current: boolean }>`
	width: 6px;
	height: 6px;
	border-radius: ${radii.round};
	background: ${({ $current }) => ($current ? darkOrange : darkGrey)};
	transition: background-color ${motion.base};
`
